import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import * as authService from '@/services/auth';

type AuthContextType = {
    token: string | null;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void> | void;
    user: { id?: string; name?: string; email?: string } | null;
    isAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAdminAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAdminAuth must be used within AuthProvider');
    return ctx;
};

export const AdminAuthProvider = ({ children }: { children: ReactNode }) => {
    const [token, setToken] = useState<string | null>(() => {
        try {
            return localStorage.getItem('admin_token');
        } catch (e) {
            return null;
        }
    });
    const [user, setUser] = useState<{ id?: string; name?: string; email?: string } | null>(() => {
        try {
            const raw = localStorage.getItem('admin_user');
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            return null;
        }
    });

    useEffect(() => {
        try {
            if (token) localStorage.setItem('admin_token', token);
            else localStorage.removeItem('admin_token');
        } catch (e) {
            // ignore
        }
    }, [token]);

    useEffect(() => {
        try {
            if (user) localStorage.setItem('admin_user', JSON.stringify(user));
            else localStorage.removeItem('admin_user');
        } catch (e) {
            // ignore
        }
    }, [user]);

    const login = async (email: string, password: string) => {
        const data = await authService.login(email, password);
        if (!data) throw new Error('Login failed');
        const access = data.tokens?.access?.token || data.access?.token || null;
        setToken(access);
        // store minimal user info if returned
        if (data.user) setUser({ id: data.user.id || data.user._id, name: data.user.name, email: data.user.email });
    };

    const logout = async () => {
        try {
            // optionally inform backend (if refresh token is stored elsewhere)
            await authService.logout();
        } catch (err) {
            // ignore network errors during logout
        }
        setToken(null);
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ token, login, logout, user, isAuthenticated: !!token }}>{children}</AuthContext.Provider>
    );
};
